import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import {
  BarChart3, ShoppingCart, DollarSign, TrendingUp, TrendingDown, Users, Package,
  Clock, CreditCard, Banknote, QrCode, XCircle, AlertTriangle, Award, Download, FileSpreadsheet
} from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';

const fmt = (v) => new Intl.NumberFormat('id-ID').format(Math.round(v || 0));
const fmtRp = (v) => `Rp ${fmt(v)}`;
const fmtDate = (s) => { try { return new Date(s).toLocaleDateString('id-ID', { day: '2-digit', month: 'short' }); } catch { return s; } };
const fmtDateFull = (s) => { try { return new Date(s).toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' }); } catch { return s; } };
const COLORS = ['#2D6CDF', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

const TAB_LABELS = { sales: 'Penjualan', shifts: 'Shift', cashiers: 'Kasir', products: 'Produk', payments: 'Pembayaran', voids: 'Void' };
const PERIOD_LABELS = { '7d': '7 Hari', '30d': '30 Hari', '90d': '90 Hari', '1y': '1 Tahun', all: 'Semua' };

function getTabExportData(tab, data) {
  switch (tab) {
    case 'sales': {
      const s = data.sales?.summary || {};
      const summaryRows = [['Total Revenue', fmtRp(s.total_revenue)], ['Transaksi', fmt(s.total_transactions)], ['Rata-rata', fmtRp(s.average_transaction)], ['Item Terjual', fmt(s.total_items)], ['Diskon', fmtRp(s.total_discount)], ['Pajak', fmtRp(s.total_tax)]];
      const tables = [{ title: 'Ringkasan Penjualan', headers: ['Metrik', 'Nilai'], rows: summaryRows }];
      const top = data.sales?.top_products || [];
      if (top.length) tables.push({ title: 'Top 10 Produk', headers: ['#', 'Produk', 'Qty', 'Revenue'], rows: top.map((p, i) => [i + 1, p.product_name, p.quantity, fmtRp(p.revenue)]) });
      const pb = data.sales?.payment_breakdown || [];
      if (pb.length) tables.push({ title: 'Breakdown Pembayaran', headers: ['Metode', 'Jumlah', 'Tx', '%'], rows: pb.map(m => [m.method, fmtRp(m.amount), m.count, `${m.percentage}%`]) });
      const daily = data.sales?.daily_trend || [];
      if (daily.length) tables.push({ title: 'Tren Harian', headers: ['Tanggal', 'Revenue', 'Transaksi'], rows: daily.map(d => [fmtDateFull(d.date), fmtRp(d.revenue), d.transactions]) });
      return tables;
    }
    case 'shifts': {
      const d = data.shifts || {};
      const summaryRows = [['Total Shift', d.total_shifts || 0], ['Avg Revenue/Shift', fmtRp(d.avg_revenue_per_shift)], ['Total Selisih Kas', fmtRp(d.total_cash_difference)], ['Avg Durasi', `${d.avg_shift_duration_hours || 0} jam`]];
      const tables = [{ title: 'Ringkasan Shift', headers: ['Metrik', 'Nilai'], rows: summaryRows }];
      const shifts = d.shifts || [];
      if (shifts.length) tables.push({ title: 'Detail Shift', headers: ['Tanggal', 'Kasir', 'Kas Awal', 'Kas Akhir', 'Expected', 'Selisih', 'Revenue', 'Tx'], rows: shifts.map(s => [fmtDateFull(s.opened_at), s.cashier_name, fmtRp(s.opening_cash), s.closing_cash != null ? fmtRp(s.closing_cash) : '-', s.expected_cash != null ? fmtRp(s.expected_cash) : '-', s.cash_difference != null ? fmtRp(s.cash_difference) : '-', fmtRp(s.total_revenue), s.total_transactions || 0]) });
      return tables;
    }
    case 'cashiers': {
      const cashiers = data.cashiers?.cashiers || [];
      return [{ title: 'Performa Kasir', headers: ['#', 'Kasir', 'Shift', 'Transaksi', 'Revenue', 'Avg Tx', 'Item', 'Void'], rows: cashiers.map((c, i) => [i + 1, c.cashier_name, c.total_shifts, c.total_transactions, fmtRp(c.total_revenue), fmtRp(c.avg_transaction_value), c.total_items_sold, c.total_voids]) }];
    }
    case 'products': {
      const d = data.products || {};
      const tables = [];
      const products = d.products || [];
      if (products.length) tables.push({ title: 'Penjualan Per Produk', headers: ['#', 'Produk', 'SKU', 'Qty', 'Revenue', 'Avg Harga', 'Muncul di Tx'], rows: products.map((p, i) => [i + 1, p.product_name, p.sku || '-', p.quantity_sold, fmtRp(p.revenue), fmtRp(p.avg_price), p.times_in_transactions]) });
      const slow = d.slow_movers || [];
      if (slow.length) tables.push({ title: 'Produk Lambat', headers: ['Produk', 'SKU', 'Stok', 'Harga'], rows: slow.map(p => [p.name, p.sku || '-', p.stock, fmtRp(p.price)]) });
      return tables;
    }
    case 'payments': {
      const d = data.payments || {};
      const methods = d.methods || [];
      const tables = [];
      if (methods.length) tables.push({ title: 'Metode Pembayaran', headers: ['Metode', 'Jumlah', 'Tx', '%', 'Growth'], rows: methods.map(m => [m.method, fmtRp(m.amount), m.count, `${m.percentage}%`, m.growth_pct !== undefined ? `${m.growth_pct}%` : '-']) });
      const daily = d.daily_trend || [];
      if (daily.length) tables.push({ title: 'Tren Harian Pembayaran', headers: ['Tanggal', 'Tunai', 'QRIS', 'Debit', 'Kredit'], rows: daily.map(d => [fmtDateFull(d.date), fmtRp(d.cash), fmtRp(d.qris), fmtRp(d.debit), fmtRp(d.credit)]) });
      return tables;
    }
    case 'voids': {
      const d = data.voids || {};
      const summaryRows = [['Total Void', d.total_voids || 0], ['Nominal Void', fmtRp(d.total_void_amount)], ['Void Rate', `${d.void_rate || 0}%`], ['Total Transaksi', d.total_transactions || 0]];
      const tables = [{ title: 'Ringkasan Void', headers: ['Metrik', 'Nilai'], rows: summaryRows }];
      const voids = d.voids || [];
      if (voids.length) tables.push({ title: 'Transaksi Void', headers: ['No Order', 'Tanggal', 'Nominal', 'Kasir', 'Alasan'], rows: voids.map(v => [v.order_number, fmtDateFull(v.void_at || v.created_at), fmtRp(v.total), v.pos_cashier_name || '-', v.void_reason || '-']) });
      return tables;
    }
    default: return [];
  }
}

async function exportPDF(tab, period, data, toast) {
  try {
    const { default: jsPDF } = await import('jspdf');
    await import('jspdf-autotable');
    const doc = new jsPDF('p', 'mm', 'a4');
    const tables = getTabExportData(tab, data);
    const title = `Laporan POS - ${TAB_LABELS[tab]} (${PERIOD_LABELS[period]})`;
    const date = new Date().toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' });

    doc.setFontSize(16);
    doc.text(title, 14, 20);
    doc.setFontSize(9);
    doc.setTextColor(100);
    doc.text(`Dicetak: ${date}`, 14, 27);
    doc.setTextColor(0);

    let y = 35;
    tables.forEach((t, idx) => {
      if (y > 250) { doc.addPage(); y = 20; }
      doc.setFontSize(11);
      doc.text(t.title, 14, y);
      y += 3;
      doc.autoTable({
        startY: y,
        head: [t.headers],
        body: t.rows.map(r => r.map(c => String(c ?? ''))),
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [15, 82, 255], textColor: 255 },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        margin: { left: 14, right: 14 },
        didDrawPage: () => {
          doc.setFontSize(7);
          doc.setTextColor(150);
          doc.text(`Tokomoe POS Report - ${date}`, 14, doc.internal.pageSize.height - 8);
          doc.text(`Halaman ${doc.internal.getNumberOfPages()}`, doc.internal.pageSize.width - 30, doc.internal.pageSize.height - 8);
          doc.setTextColor(0);
        },
      });
      y = doc.lastAutoTable.finalY + 10;
    });

    doc.save(`pos_${tab}_${period}_${new Date().toISOString().slice(0, 10)}.pdf`);
    toast({ title: 'PDF berhasil diunduh' });
  } catch (err) {
    toast({ title: 'Gagal export PDF', description: err.message, variant: 'destructive' });
  }
}

async function exportExcel(tab, period, data, toast) {
  try {
    const XLSX = await import('xlsx');
    const wb = XLSX.utils.book_new();
    const tables = getTabExportData(tab, data);

    tables.forEach((t) => {
      const sheetData = [t.headers, ...t.rows.map(r => r.map(c => {
        if (typeof c === 'string' && c.startsWith('Rp ')) {
          const num = Number(c.replace(/[^0-9-]/g, ''));
          return isNaN(num) ? c : num;
        }
        return typeof c === 'string' && !isNaN(Number(c)) ? Number(c) : c;
      }))];
      const ws = XLSX.utils.aoa_to_sheet(sheetData);
      // Auto column width
      ws['!cols'] = t.headers.map((h, i) => ({
        wch: Math.max(h.length, ...t.rows.map(r => String(r[i] ?? '').length)) + 2
      }));
      const sheetName = t.title.substring(0, 31);
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    });

    XLSX.writeFile(wb, `pos_${tab}_${period}_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast({ title: 'Excel berhasil diunduh' });
  } catch (err) {
    toast({ title: 'Gagal export Excel', description: err.message, variant: 'destructive' });
  }
}

function StatCard({ icon: Icon, label, value, sub, color = 'text-[#2D6CDF]', bg = 'bg-blue-50' }) {
  return (
    <Card className="border-slate-200 shadow-sm">
      <CardContent className="p-4">
        <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center mb-2`}>
          <Icon size={18} className={color} />
        </div>
        <p className="text-xl font-bold text-slate-900">{value}</p>
        <p className="text-xs text-slate-500 mt-0.5">{label}</p>
        {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
      </CardContent>
    </Card>
  );
}

export default function POSReports() {
  const { toast } = useToast();
  const [period, setPeriod] = useState('30d');
  const [tab, setTab] = useState('sales');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const endpoints = {
        sales: `/tenant/pos/reports/daily-sales?period=${period}`,
        shifts: `/tenant/pos/reports/shift-summary?period=${period}`,
        cashiers: `/tenant/pos/reports/cashier-performance?period=${period}`,
        products: `/tenant/pos/reports/product-sales?period=${period}`,
        payments: `/tenant/pos/reports/payment-methods?period=${period}`,
        voids: `/tenant/pos/reports/voids?period=${period}`,
        hourly: `/tenant/pos/reports/hourly-analysis?period=${period}`,
      };
      const results = {};
      // Fetch current tab + basic sales data
      const toFetch = [tab];
      if (tab !== 'sales') toFetch.push('sales');

      const responses = await Promise.all(toFetch.map(key => api.get(endpoints[key]).catch(() => ({ data: {} }))));
      toFetch.forEach((key, i) => { results[key] = responses[i].data; });

      setData(prev => ({ ...prev, ...results }));
    } catch { /* interceptor */ }
    setLoading(false);
  }, [period, tab]);

  useEffect(() => { load(); }, [load]);

  const sales = data.sales || {};
  const s = sales.summary || {};

  if (loading && !data.sales) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D6CDF]" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="pos-reports-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><BarChart3 size={24} /> Laporan POS</h1>
          <p className="text-muted-foreground mt-1">Analitik lengkap penjualan kasir</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => exportPDF(tab, period, data, toast)} disabled={loading}>
            <Download size={14} className="mr-1.5" /> PDF
          </Button>
          <Button variant="outline" size="sm" onClick={() => exportExcel(tab, period, data, toast)} disabled={loading}>
            <FileSpreadsheet size={14} className="mr-1.5" /> Excel
          </Button>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[130px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">7 Hari</SelectItem>
              <SelectItem value="30d">30 Hari</SelectItem>
              <SelectItem value="90d">90 Hari</SelectItem>
              <SelectItem value="1y">1 Tahun</SelectItem>
              <SelectItem value="all">Semua</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="sales">Penjualan</TabsTrigger>
          <TabsTrigger value="shifts">Shift</TabsTrigger>
          <TabsTrigger value="cashiers">Kasir</TabsTrigger>
          <TabsTrigger value="products">Produk</TabsTrigger>
          <TabsTrigger value="payments">Pembayaran</TabsTrigger>
          <TabsTrigger value="voids">Void</TabsTrigger>
        </TabsList>

        {/* ── Tab 1: Penjualan ── */}
        <TabsContent value="sales" className="space-y-6">
          <div className="grid grid-cols-3 lg:grid-cols-6 gap-3">
            <StatCard icon={DollarSign} label="Total Revenue" value={fmtRp(s.total_revenue)} color="text-emerald-600" bg="bg-emerald-50" />
            <StatCard icon={ShoppingCart} label="Transaksi" value={fmt(s.total_transactions)} color="text-[#2D6CDF]" bg="bg-blue-50" />
            <StatCard icon={TrendingUp} label="Rata-rata" value={fmtRp(s.average_transaction)} color="text-violet-600" bg="bg-violet-50" />
            <StatCard icon={Package} label="Item Terjual" value={fmt(s.total_items)} color="text-amber-600" bg="bg-amber-50" />
            <StatCard icon={DollarSign} label="Total Diskon" value={fmtRp(s.total_discount)} color="text-orange-600" bg="bg-orange-50" />
            <StatCard icon={DollarSign} label="Total Pajak" value={fmtRp(s.total_tax)} color="text-slate-600" bg="bg-slate-100" />
          </div>

          {/* Daily Revenue Trend */}
          {(sales.daily_trend || []).length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-sm">Tren Revenue Harian</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart data={sales.daily_trend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={v => `${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => [fmtRp(v), '']} labelFormatter={fmtDate} />
                    <Area type="monotone" dataKey="revenue" stroke="#2D6CDF" fill="#2D6CDF" fillOpacity={0.1} />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Hourly Distribution + Top Products */}
          <div className="grid grid-cols-2 gap-4">
            {(sales.hourly_distribution || []).length > 0 && (
              <Card>
                <CardHeader><CardTitle className="text-sm">Distribusi Per Jam</CardTitle></CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={sales.hourly_distribution}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="hour" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip formatter={v => [fmt(v), '']} />
                      <Bar dataKey="transactions" fill="#2D6CDF" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
            <Card>
              <CardHeader><CardTitle className="text-sm">Top 10 Produk</CardTitle></CardHeader>
              <CardContent className="p-0">
                {(sales.top_products || []).length === 0 ? (
                  <p className="p-6 text-center text-slate-400 text-sm">Belum ada data</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50"><tr><th className="p-2 text-left">#</th><th className="p-2 text-left">Produk</th><th className="p-2 text-right">Qty</th><th className="p-2 text-right">Revenue</th></tr></thead>
                    <tbody>
                      {(sales.top_products || []).map((p, i) => (
                        <tr key={i} className="border-t"><td className="p-2 text-slate-400">{i + 1}</td><td className="p-2">{p.product_name}</td><td className="p-2 text-right">{p.quantity}</td><td className="p-2 text-right font-medium">{fmtRp(p.revenue)}</td></tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Payment Breakdown */}
          {(sales.payment_breakdown || []).length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-sm">Breakdown Pembayaran</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-3">
                  {sales.payment_breakdown.map(m => {
                    const icons = { cash: Banknote, qris: QrCode, debit: CreditCard, credit: CreditCard };
                    const colors = { cash: 'text-emerald-600 bg-emerald-50', qris: 'text-violet-600 bg-violet-50', debit: 'text-blue-600 bg-blue-50', credit: 'text-amber-600 bg-amber-50' };
                    const Icon = icons[m.method] || CreditCard;
                    const [tc, bg] = (colors[m.method] || 'text-slate-600 bg-slate-50').split(' ');
                    return (
                      <div key={m.method} className={`p-3 ${bg} rounded-lg`}>
                        <div className="flex items-center gap-2 mb-1"><Icon size={16} className={tc} /><span className="font-medium capitalize text-sm">{m.method}</span></div>
                        <p className="text-lg font-bold">{fmtRp(m.amount)}</p>
                        <p className="text-xs text-slate-500">{m.count} tx · {m.percentage}%</p>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ── Tab 2: Shift ── */}
        <TabsContent value="shifts" className="space-y-6">
          {(() => {
            const d = data.shifts || {};
            return (<>
              <div className="grid grid-cols-4 gap-3">
                <StatCard icon={Clock} label="Total Shift" value={d.total_shifts || 0} />
                <StatCard icon={DollarSign} label="Avg Revenue/Shift" value={fmtRp(d.avg_revenue_per_shift)} color="text-emerald-600" bg="bg-emerald-50" />
                <StatCard icon={Banknote} label="Total Selisih Kas" value={fmtRp(d.total_cash_difference)}
                  color={(d.total_cash_difference || 0) < 0 ? 'text-red-600' : 'text-emerald-600'}
                  bg={(d.total_cash_difference || 0) < 0 ? 'bg-red-50' : 'bg-emerald-50'} />
                <StatCard icon={Clock} label="Avg Durasi" value={`${d.avg_shift_duration_hours || 0} jam`} color="text-violet-600" bg="bg-violet-50" />
              </div>
              <Card>
                <CardContent className="p-0">
                  {(d.shifts || []).length === 0 ? <p className="p-8 text-center text-slate-400">Belum ada shift</p> : (
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 border-b"><tr>
                        <th className="p-3 text-left">Tanggal</th><th className="p-3 text-left">Kasir</th>
                        <th className="p-3 text-right">Kas Awal</th><th className="p-3 text-right">Kas Akhir</th>
                        <th className="p-3 text-right">Expected</th><th className="p-3 text-right">Selisih</th>
                        <th className="p-3 text-right">Revenue</th><th className="p-3 text-center">Tx</th>
                      </tr></thead>
                      <tbody>
                        {(d.shifts || []).slice(0, 30).map(s => (
                          <tr key={s.id} className="border-t">
                            <td className="p-3">{fmtDate(s.opened_at)}</td>
                            <td className="p-3">{s.cashier_name}</td>
                            <td className="p-3 text-right">{fmtRp(s.opening_cash)}</td>
                            <td className="p-3 text-right">{s.closing_cash !== null ? fmtRp(s.closing_cash) : '-'}</td>
                            <td className="p-3 text-right">{s.expected_cash !== null ? fmtRp(s.expected_cash) : '-'}</td>
                            <td className={`p-3 text-right font-medium ${(s.cash_difference || 0) < 0 ? 'text-red-600' : ''}`}>{s.cash_difference !== null ? fmtRp(s.cash_difference) : '-'}</td>
                            <td className="p-3 text-right font-semibold text-emerald-600">{fmtRp(s.total_revenue)}</td>
                            <td className="p-3 text-center">{s.total_transactions || 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </CardContent>
              </Card>
            </>);
          })()}
        </TabsContent>

        {/* ── Tab 3: Kasir ── */}
        <TabsContent value="cashiers" className="space-y-6">
          {(() => {
            const cashiers = data.cashiers?.cashiers || [];
            return cashiers.length === 0 ? (
              <Card><CardContent className="py-16 text-center text-slate-400"><Users size={48} className="mx-auto mb-2" /><p>Belum ada data kasir</p></CardContent></Card>
            ) : (
              <Card>
                <CardContent className="p-0">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b"><tr>
                      <th className="p-3 text-left">#</th><th className="p-3 text-left">Kasir</th>
                      <th className="p-3 text-center">Shift</th><th className="p-3 text-center">Transaksi</th>
                      <th className="p-3 text-right">Revenue</th><th className="p-3 text-right">Avg Tx</th>
                      <th className="p-3 text-center">Item</th><th className="p-3 text-center">Void</th>
                    </tr></thead>
                    <tbody>
                      {cashiers.map((c, i) => (
                        <tr key={c.cashier_id} className="border-t">
                          <td className="p-3">
                            {i === 0 ? <Badge className="bg-amber-100 text-amber-700"><Award size={12} className="mr-1" /> 1</Badge> : i + 1}
                          </td>
                          <td className="p-3 font-medium">{c.cashier_name}</td>
                          <td className="p-3 text-center">{c.total_shifts}</td>
                          <td className="p-3 text-center">{c.total_transactions}</td>
                          <td className="p-3 text-right font-semibold text-emerald-600">{fmtRp(c.total_revenue)}</td>
                          <td className="p-3 text-right">{fmtRp(c.avg_transaction_value)}</td>
                          <td className="p-3 text-center">{c.total_items_sold}</td>
                          <td className="p-3 text-center">{c.total_voids > 0 ? <span className="text-red-600">{c.total_voids}</span> : '0'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            );
          })()}
        </TabsContent>

        {/* ── Tab 4: Produk ── */}
        <TabsContent value="products" className="space-y-6">
          {(() => {
            const d = data.products || {};
            const products = d.products || [];
            const cats = d.category_breakdown || [];
            const slow = d.slow_movers || [];
            return (<>
              {products.length === 0 ? (
                <Card><CardContent className="py-16 text-center text-slate-400"><Package size={48} className="mx-auto mb-2" /><p>Belum ada data penjualan produk</p></CardContent></Card>
              ) : (
                <>
                  {/* Product Sales Table */}
                  <Card>
                    <CardHeader><CardTitle className="text-sm">Penjualan Per Produk</CardTitle></CardHeader>
                    <CardContent className="p-0">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 border-b"><tr>
                          <th className="p-3 text-left">#</th><th className="p-3 text-left">Produk</th>
                          <th className="p-3 text-left">SKU</th><th className="p-3 text-right">Qty</th>
                          <th className="p-3 text-right">Revenue</th><th className="p-3 text-right">Avg Harga</th>
                          <th className="p-3 text-center">Muncul di Tx</th>
                        </tr></thead>
                        <tbody>
                          {products.slice(0, 30).map((p, i) => (
                            <tr key={p.product_id} className="border-t">
                              <td className="p-3 text-slate-400">{i + 1}</td>
                              <td className="p-3 font-medium">{p.product_name}</td>
                              <td className="p-3 text-slate-400 font-mono text-xs">{p.sku || '-'}</td>
                              <td className="p-3 text-right font-bold">{p.quantity_sold}</td>
                              <td className="p-3 text-right font-semibold text-emerald-600">{fmtRp(p.revenue)}</td>
                              <td className="p-3 text-right">{fmtRp(p.avg_price)}</td>
                              <td className="p-3 text-center">{p.times_in_transactions}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </CardContent>
                  </Card>

                  {/* Category PieChart + Slow Movers */}
                  <div className="grid grid-cols-2 gap-4">
                    {cats.length > 0 && (
                      <Card>
                        <CardHeader><CardTitle className="text-sm">Revenue per Kategori</CardTitle></CardHeader>
                        <CardContent>
                          <ResponsiveContainer width="100%" height={200}>
                            <PieChart>
                              <Pie data={cats} dataKey="revenue" nameKey="category" cx="50%" cy="50%" outerRadius={70} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                                {cats.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                              </Pie>
                              <Tooltip formatter={v => fmtRp(v)} />
                            </PieChart>
                          </ResponsiveContainer>
                        </CardContent>
                      </Card>
                    )}
                    <Card>
                      <CardHeader><CardTitle className="text-sm flex items-center gap-2"><AlertTriangle size={14} className="text-amber-500" /> Produk Lambat (Tidak Terjual)</CardTitle></CardHeader>
                      <CardContent className="p-0">
                        {slow.length === 0 ? <p className="p-6 text-center text-slate-400 text-sm">Semua produk terjual!</p> : (
                          <table className="w-full text-sm">
                            <thead className="bg-slate-50"><tr><th className="p-2 text-left">Produk</th><th className="p-2 text-left">SKU</th><th className="p-2 text-right">Stok</th><th className="p-2 text-right">Harga</th></tr></thead>
                            <tbody>
                              {slow.map(p => (
                                <tr key={p.id} className="border-t"><td className="p-2">{p.name}</td><td className="p-2 text-slate-400 text-xs">{p.sku || '-'}</td><td className="p-2 text-right">{p.stock}</td><td className="p-2 text-right">{fmtRp(p.price)}</td></tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                </>
              )}
            </>);
          })()}
        </TabsContent>

        {/* ── Tab 5: Pembayaran ── */}
        <TabsContent value="payments" className="space-y-6">
          {(() => {
            const d = data.payments || {};
            const methods = d.methods || [];
            const daily = d.daily_trend || [];
            const methodIcons = { cash: Banknote, qris: QrCode, debit: CreditCard, credit: CreditCard };
            const methodColors = { cash: 'emerald', qris: 'violet', debit: 'blue', credit: 'amber' };
            return (<>
              <div className="grid grid-cols-4 gap-3">
                {methods.map(m => {
                  const Icon = methodIcons[m.method] || CreditCard;
                  const c = methodColors[m.method] || 'slate';
                  return (
                    <Card key={m.method}>
                      <CardContent className="p-4">
                        <div className={`w-9 h-9 rounded-lg bg-${c}-50 flex items-center justify-center mb-2`}>
                          <Icon size={18} className={`text-${c}-600`} />
                        </div>
                        <p className="text-lg font-bold">{fmtRp(m.amount)}</p>
                        <p className="text-xs text-slate-500 capitalize">{m.method} · {m.count} tx · {m.percentage}%</p>
                        {m.growth_pct !== undefined && (
                          <div className={`flex items-center gap-1 mt-1 text-xs ${m.growth_pct >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {m.growth_pct >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                            {m.growth_pct >= 0 ? '+' : ''}{m.growth_pct}% vs periode sebelumnya
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {daily.length > 0 && (
                <Card>
                  <CardHeader><CardTitle className="text-sm">Tren Harian per Metode</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={daily}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 10 }} />
                        <YAxis tickFormatter={v => `${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 10 }} />
                        <Tooltip formatter={v => fmtRp(v)} labelFormatter={fmtDate} />
                        <Bar dataKey="cash" stackId="a" fill="#10b981" name="Tunai" />
                        <Bar dataKey="qris" stackId="a" fill="#8b5cf6" name="QRIS" />
                        <Bar dataKey="debit" stackId="a" fill="#2D6CDF" name="Debit" />
                        <Bar dataKey="credit" stackId="a" fill="#f59e0b" name="Kredit" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}
            </>);
          })()}
        </TabsContent>

        {/* ── Tab 6: Void ── */}
        <TabsContent value="voids" className="space-y-6">
          {(() => {
            const d = data.voids || {};
            return (<>
              <div className="grid grid-cols-3 gap-4">
                <StatCard icon={XCircle} label="Total Void" value={d.total_voids || 0} color="text-red-600" bg="bg-red-50" />
                <StatCard icon={DollarSign} label="Total Nominal Void" value={fmtRp(d.total_void_amount)} color="text-red-600" bg="bg-red-50" />
                <StatCard icon={AlertTriangle} label="Void Rate" value={`${d.void_rate || 0}%`}
                  sub={`dari ${d.total_transactions || 0} total transaksi`}
                  color={(d.void_rate || 0) > 5 ? 'text-red-600' : 'text-amber-600'}
                  bg={(d.void_rate || 0) > 5 ? 'bg-red-50' : 'bg-amber-50'} />
              </div>

              {(d.void_trend || []).length > 0 && (
                <Card>
                  <CardHeader><CardTitle className="text-sm">Tren Void</CardTitle></CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={200}>
                      <LineChart data={d.void_trend}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tickFormatter={fmtDate} tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 10 }} />
                        <Tooltip formatter={v => [v, '']} labelFormatter={fmtDate} />
                        <Line type="monotone" dataKey="voids" stroke="#ef4444" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardHeader><CardTitle className="text-sm">Transaksi Void</CardTitle></CardHeader>
                <CardContent className="p-0">
                  {(d.voids || []).length === 0 ? <p className="p-8 text-center text-slate-400">Tidak ada void</p> : (
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 border-b"><tr>
                        <th className="p-3 text-left">No Order</th><th className="p-3 text-left">Tanggal</th>
                        <th className="p-3 text-right">Nominal</th><th className="p-3 text-left">Kasir</th>
                        <th className="p-3 text-left">Alasan</th>
                      </tr></thead>
                      <tbody>
                        {(d.voids || []).map(v => (
                          <tr key={v.id} className="border-t">
                            <td className="p-3 font-mono text-xs">{v.order_number}</td>
                            <td className="p-3">{fmtDate(v.void_at || v.created_at)}</td>
                            <td className="p-3 text-right font-medium text-red-600">{fmtRp(v.total)}</td>
                            <td className="p-3">{v.pos_cashier_name || '-'}</td>
                            <td className="p-3 text-slate-500">{v.void_reason || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </CardContent>
              </Card>
            </>);
          })()}
        </TabsContent>
      </Tabs>
    </div>
  );
}
